//@ts-check
import * as THREE from "three";
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';

/**
 * @module Label
 * @description This module provides functions to create and manage 2D labels in a 3D scene using THREE.js and CSS2DRenderer.
 */


/**
 * Labels multiple elements
 * @param {Array<string>} element_Ids - Element Ids
 * @param {Array<string>} labels - Labels
 * @param {Array<Number>} colors - Hex Colors 
 * @returns {Object} Return success or error messages  
 * { status:  mesage: }
 */
export function labelElements(element_Ids, labels, colors) {
  var scope = this;
  this.clearAllLabels();
  try {
    if (scope.modelScene.children.length != 0 && element_Ids.length == labels.length && element_Ids.length == colors.length) {
      for (let i = 0; i < element_Ids.length; i++) {
        scope.labelElement(element_Ids[i], labels[i], colors[i]);
      }
      
      if(scope.clipActive && scope.clippingPlanes){
        this.clipLabels(); 
      }
      var returnValue = {
        "status": "success",
        "message": "Label elements API"
      }
      return returnValue;
    }
    else {
      var returnValue = {
        "status": "failed",
        "message": "ERROR : Either model is not loaded in viewer or element id array and label array is not same."
      }
      return returnValue;
    }
  }
  catch (err) {
    var returnValue = {
      "status": "failed",
      "message": "System Error : " + err.message
    }
    return returnValue;
  }
}


/**
 * Labels single element
 * @param {string} element_ID - Element id
 * @param {string} label - Label
 * @param {Number} color - Hex Color
 * @returns {Object} Return success or error messages  
 * { status:  mesage: }
 */
export function labelElement(element_ID, label, color) {
  var scope = this;
  try {
    if (scope.modelScene.children.length != 0) {
      let child = scope.elementsMap.get(element_ID);
      if (child instanceof THREE.Mesh) {
        scope.makeInstance(label, element_ID, child, color);
      }
      var returnValue = {
        "status": "success",
        "message": "Label element API"
      }
      return returnValue;
    }
    else {
      var returnValue = {
        "status": "failed",
        "message": "ERROR : Load the model in viewer."
      }
      return returnValue;
    }
  }
  catch (err) {
    var returnValue = {
      "status": "failed",
      "message": "System Error : " + err.message
    }
    return returnValue;
  }
}


/**
 * Make label instance
 * @param {string} label - Label
 * @param {string} element_id - Element id
 * @param {THREE.Object3D} element - Element as THREE Object
 * @param {Number} color - Hex Color 
 * @returns {CSS2DObject} Label Object
 */
export function makeInstance(label, element_id, element, color) {
  var scope = this;

  var box = new THREE.Box3();
  box.setFromObject(element);
  var center = box.getCenter(new THREE.Vector3());

  let elem = document.createElement('div');
  elem.className = 'label';
  elem.id = element_id;
  elem.textContent = scope.setDigits(label);
  elem.style['font-size'] = scope.labelSize + "px";
  elem.style['background-color'] = color.toString();
  elem.style.borderRadius = "10px";
  elem.style.padding = "0px 5px 0px 5px";
  let elemLabel = new CSS2DObject(elem);
  elemLabel.position.set(center.x, center.y, center.z);
  // elemLabel.visible = this.isObjectVisible(element);
  element.add(elemLabel);
  scope.labelVariables.push({
    content: elem,
    element: element,
    label: elemLabel,
  })
  return elemLabel;
}


/**
 * Updates the labelVariables visibility per object visible status
 */
export function labelUpdate() {
  var scope = this;
  scope.labelVariables.forEach((object) => {
    object.label.visible = this.isObjectVisible(object.element);
  });
}


/**
 * Updates all labels
 * @param {Number | string} labelSize - Label size
 */
export function updateAllLabels(labelSize) {
  var scope = this;
  scope.labelSize = labelSize;
  scope.labelVariables.forEach((object) => {
    object.content.style['font-size'] = scope.labelSize + "px";
  });
}

/**
 * Clears All Label
 */
export function clearAllLabels() {
  var scope = this;
  scope.labelVariables = [];

  scope.elementsMap.forEach((child, name)=>{
    if (child instanceof THREE.Mesh) {
      if (child.children.length > 0) child.remove(child.children[0]);
    }
  })

}

// remove single label
export function removeLabel(element_Id, render = false) {
  var scope = this;

  try {
    if (scope.modelScene.children.length != 0) {
      let child = scope.elementsMap.get(element_ID);

      if (child instanceof THREE.Mesh) {
        var label = document.querySelector("#labels");
        for (let i = 0; i < label.children.length; i++) {
          if (label.children[i].id == element_Id) {
            label.removeChild(label.children[i]);
          }
        }
      }

      if (render) {
        viewerMgrInstance.render();
      }
      var returnValue = {
        "status": "success",
        "message": "remove label API"
      }
      return returnValue;
    }
    else {
      var returnValue = {
        "status": "failed",
        "message": "ERROR : Load the model in viewer."
      }
      return returnValue;
    }
  }
  catch (err) {
    var returnValue = {
      "status": "failed",
      "message": "System Error : " + err.message
    }
    return returnValue;
  }

}

// remove multiple labels
export function removeLabels(element_Ids, render = false) {
  var scope = this;
  try {

    if (scope.modelScene.children.length != 0 && element_Ids.length >= 1) {

      for (let i = 0; i < element_Ids.length; i++) {
        scope.removeLabel(element_Ids[i]);
      }

      if (render) {
        viewerMgrInstance.render();
      }
      var returnValue = {
        "status": "success",
        "message": "Remove labels API"
      }
      return returnValue;
    }
    else {
      var returnValue = {
        "status": "failed",
        "message": "ERROR : Load the model in viewer."
      }
      return returnValue;
    }
  }
  catch (err) {
    var returnValue = {
      "status": "failed",
      "message": "System Error : " + err.message
    }
    return returnValue;
  }
}

/**
 * Manages the visibility of labels based on clipping planes
 */
export function clipLabels() {
    var scope = this;
    if (scope.labelVariables.length && scope.clipActive) {
        scope.labelVariables.forEach((labelVariable) => {
          const child = labelVariable.element;
          const label = labelVariable.label;
         // Returns true if the label is clipped by the plane, otherwise false
          const isClipped = scope.clippingPlanes.some((plane) => {
            const result = plane.distanceToPoint(labelVariable.label.position) < 0;
            return result;
          });
          if (isClipped) {
            // Remove the label if the element is clipped
            if (child.children.includes(label)) {
              child.remove(label);
            }
          } else {
            // Add the label back if the element is not clipped
            if (!child.children.includes(label)) {
              child.add(label);
            }
          }
        });
      }
}


export function restoreLabels(){
  var scope = this;
  scope.labelVariables.forEach((labelVariable) => {
    const child = labelVariable.element;
    const label = labelVariable.label;
    child.add(label);
  });
}